import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { FunnelSimpleIcon, MagnifyingGlassIcon } from '@phosphor-icons/react';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/Tooltip';
import {
    useQueryFiltersStore,
    useQueryPanelStore,
    useQueryResultsStore,
    useSourcesStore,
} from '@/stores';
import type { SuggestedQuery } from '@/stores/query-results.store';
import { useVideoSourceSelected } from '@/hooks';
import {
    DateTimeMode,
    getDateTimeLabel,
    SortOption,
    type QueryFilters,
} from '@/utils/query-filters';
import {
    buildUrlStateFromStores,
    deserializeFromUrlParams,
    getSortFromSearchParams,
    serializeToUrlParams,
    URL_PARAMS,
} from '@/utils/query-url-params';

const TIME_LADDER: DateTimeMode[] = [
    DateTimeMode.LAST_HOUR,
    DateTimeMode.LAST_3_HOURS,
    DateTimeMode.LAST_6_HOURS,
    DateTimeMode.LAST_12_HOURS,
    DateTimeMode.LAST_24_HOURS,
    DateTimeMode.LAST_3_DAYS,
    DateTimeMode.LAST_7_DAYS,
    DateTimeMode.LAST_14_DAYS,
    DateTimeMode.LAST_30_DAYS,
    DateTimeMode.ALL,
];

const CONFIDENCE_STEP = 20;

function getNextBroaderMode(mode: DateTimeMode): DateTimeMode {
    const index = TIME_LADDER.indexOf(mode);
    if (index === -1) return DateTimeMode.ALL;
    return TIME_LADDER[index + 1] ?? DateTimeMode.ALL;
}

const chipClassName =
    'flex items-center gap-2.5 min-w-[200px] rounded-lg border ' +
    'border-grey-550 bg-grey-700 px-3.5 py-2.5 text-left ' +
    'transition-colors duration-200 ease-out hover:border-grey-300';

interface BroadenSuggestion {
    key: string;
    label: string;
    onClick: () => void;
}

export function EmptyResultsSuggestions() {
    const { t } = useTranslation(I18nNamespace.QUERIES);
    const [searchParams, setSearchParams] = useSearchParams();
    const confidence = useQueryFiltersStore(
        (state) => state.filters.confidence,
    );
    const dateTime = useQueryFiltersStore((state) => state.filters.dateTime);
    const selected = useSourcesStore((state) => state.selected);
    const videoSourceSelected = useVideoSourceSelected(selected);
    const clearEvents = useQueryResultsStore((state) => state.clearEvents);
    const querySuggestions = useQueryResultsStore(
        (state) => state.querySuggestions,
    );
    const setSearchQuery = useQueryPanelStore((state) => state.setSearchQuery);

    const attachment = deserializeFromUrlParams(searchParams).attachment;

    const runSearch = (
        query: string,
        nextFilters: QueryFilters,
        nextAttachment: typeof attachment,
    ) => {
        const sourcesState = useSourcesStore.getState();
        const filtersState = useQueryFiltersStore.getState();
        const sort = searchParams.has(URL_PARAMS.SORT)
            ? getSortFromSearchParams(searchParams, SortOption.CONFIDENCE)
            : SortOption.CONFIDENCE;
        const urlState = buildUrlStateFromStores(
            query,
            {
                all: sourcesState.all,
                selected: sourcesState.selected,
                selectedFolders: sourcesState.selectedFolders,
                excluded: sourcesState.excluded,
            },
            nextFilters,
            sort,
            nextAttachment,
            filtersState.getSelectedObjectClassIds(),
        );
        const params = serializeToUrlParams(urlState, new URLSearchParams());
        params.set('_t', String(Date.now()));
        clearEvents();
        setSearchParams(params);
    };

    const runBroadenedSearch = (nextFilters: QueryFilters) =>
        runSearch(searchParams.get('q') || '', nextFilters, attachment);

    const runSuggestion = (suggestion: SuggestedQuery) => {
        setSearchQuery(suggestion.text);
        runSearch(
            suggestion.text,
            useQueryFiltersStore.getState().filters,
            suggestion.useAttachment ? attachment : undefined,
        );
    };

    const canLowerConfidence = confidence.enabled && confidence.min > 0;
    const canExtendTime =
        !videoSourceSelected && dateTime.mode !== DateTimeMode.ALL;
    const nextConfidenceMin = Math.max(0, confidence.min - CONFIDENCE_STEP);
    const nextTimeMode = getNextBroaderMode(dateTime.mode);

    const broadenSuggestions: BroadenSuggestion[] = [
        canLowerConfidence
            ? {
                  key: 'confidence',
                  label: t('emptySuggestions.lowerConfidence', {
                      min: nextConfidenceMin,
                  }),
                  onClick: () => {
                      const current = useQueryFiltersStore.getState().filters;
                      runBroadenedSearch({
                          ...current,
                          confidence: {
                              enabled: true,
                              min: nextConfidenceMin,
                              max: current.confidence.max,
                          },
                      });
                  },
              }
            : null,
        canExtendTime
            ? {
                  key: 'time',
                  label: t('emptySuggestions.extendTime', {
                      range: getDateTimeLabel({
                          mode: nextTimeMode,
                      }).toLowerCase(),
                  }),
                  onClick: () => {
                      const current = useQueryFiltersStore.getState().filters;
                      runBroadenedSearch({
                          ...current,
                          dateTime: { mode: nextTimeMode },
                      });
                  },
              }
            : null,
    ].filter(
        (suggestion): suggestion is BroadenSuggestion => suggestion !== null,
    );

    if (querySuggestions.length === 0 && broadenSuggestions.length === 0)
        return null;

    return (
        <div className="pl-6 pr-4 pt-3 flex flex-wrap gap-3 animate-fadeIn">
            {querySuggestions.map((suggestion) => (
                <button
                    key={suggestion.text}
                    type="button"
                    className={chipClassName}
                    onClick={() => runSuggestion(suggestion)}
                >
                    <MagnifyingGlassIcon
                        size={14}
                        className="shrink-0 text-grey-100"
                    />
                    <span className="flex flex-col items-start">
                        <span className="text-xs text-grey-300">
                            {t('emptySuggestions.tryInstead')}
                        </span>
                        <span className="text-sm text-white-100">
                            {suggestion.text}
                        </span>
                    </span>
                    {suggestion.useAttachment && attachment?.url && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="shrink-0">
                                    <img
                                        src={attachment.url}
                                        alt=""
                                        className="h-4 w-4 rounded-sm object-cover ring-1 ring-grey-600"
                                    />
                                </span>
                            </TooltipTrigger>
                            <TooltipContent
                                side="top"
                                sideOffset={6}
                                variant="panel"
                                className="p-1"
                            >
                                <img
                                    src={attachment.url}
                                    alt={attachment.filename}
                                    className="h-32 w-32 rounded-lg bg-grey-800 object-contain shadow-lg ring-1 ring-grey-600"
                                />
                            </TooltipContent>
                        </Tooltip>
                    )}
                </button>
            ))}
            {broadenSuggestions.map((suggestion) => (
                <button
                    key={suggestion.key}
                    type="button"
                    className={chipClassName}
                    onClick={suggestion.onClick}
                >
                    <FunnelSimpleIcon
                        size={14}
                        className="shrink-0 text-grey-100"
                    />
                    <span className="flex flex-col items-start">
                        <span className="text-xs text-grey-300">
                            {t('emptySuggestions.broaden')}
                        </span>
                        <span className="text-sm text-white-100">
                            {suggestion.label}
                        </span>
                    </span>
                </button>
            ))}
        </div>
    );
}
